<script>
import appConfig from '@/app.config'
import PageHeader from '@/components/page-header'
import Layout from '@/router/layouts/main'
import AssemblePackage from './AssemblePackage'
import { getSmsClient } from '@/services/sms'

const smsAxios = getSmsClient()

const packageFilters = [
  { value: 'all', text: 'All' },
  { value: 'in-stock', text: 'In Stock', filter: "location like '%Stock'" },
  { value: 'with-technicians', text: 'With technicians', filter: 'technician_id is not null' },
  { value: 'in-transit', text: 'In transit', filter: "location like '%Transit'" },
  { value: 'qc-pending', text: 'QC Pending', filter: "qc_status eq 'QC_PENDING'" },
  { value: 'qc-failed', text: 'QC Failed', filter: "qc_status eq 'QC_FAILED'" },
]

const qcPendingActions = [
  { status: 'QC_PASS', text: 'QC Pass', variant: 'outline-success' },
  { status: 'QC_FAILED', text: 'QC Failed', variant: 'outline-danger' },
]

const qcFailedActions = [
  { status: 'QC_PENDING', text: 'QC Pending', variant: 'outline-warning' },
]

export default {
  page: {
    title: 'Package',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    Layout,
    PageHeader,
    AssemblePackage,
  },
  data() {
    return {
      selectedFilter: 'all',
      filters: packageFilters.map(({ value, text }) => ({ value, text })),
      packages: [],
      summary: {
        in_stock_sby: 0,
        in_stock_jkt: 0,
        allocated_in_warehouse: 0,
        with_technicians: 0,
        in_transit: 0,
        not_shippable: 0,
      },
      search: '',
      searchTimer: null,
      isLoading: false,
      isSummaryLoading: false,
      updatingPackage: {},
      totalRows: 0,
      totalPages: 0,
      currentPage: 1,
      perPage: 10,
      pageOptions: [10, 25, 50, 100],
    }
  },
  computed: {
    title() {
      return this.$t('planner.column.package')
    },
    description() {
      return this.$t('planner.subColumn.package')
    },
    packageSummary() {
      return [
        { value: this.summary.in_stock_sby, label: 'In Stock, Sby' },
        { value: this.summary.in_stock_jkt, label: 'In Stock, Jkt' },
        { value: this.summary.allocated_in_warehouse, label: 'Allocated, in warehouse' },
        { value: this.summary.with_technicians, label: 'With technicians' },
        { value: this.summary.in_transit, label: 'In transit' },
        { value: this.summary.not_shippable, label: 'Not shippable' },
      ]
    },
    paginationStart() {
      if (!this.totalRows) return 0
      return (this.currentPage - 1) * this.perPage + 1
    },
    paginationEnd() {
      return Math.min(this.currentPage * this.perPage, this.totalRows)
    },
  },
  created() {
    this.fetchPackageSummary()
    this.fetchPackages()
  },
  methods: {
    async fetchPackageSummary() {
      this.isSummaryLoading = true
      const res = await smsAxios.get('/package/summary').catch((error) => {
        this.showError(error)
      })

      if (res?.data?.data) this.summary = { ...this.summary, ...res.data.data }
      this.isSummaryLoading = false
    },
    async fetchPackages() {
      this.isLoading = true
      const params = this.buildPackageListParams()

      const res = await smsAxios.get('/package', { params }).catch((error) => {
        this.showError(error)
      })

      if (res) {
        this.packages = res.data?.data?.paginated_result || []
        this.totalRows = res.data?.metadata?.total_count || 0
        this.totalPages = res.data?.metadata?.total_page || 0
        this.currentPage = res.data?.metadata?.page || this.currentPage
      }

      this.isLoading = false
    },
    showError(error) {
      const message = error?.response?.data?.message || error?.message || 'Failed to load package data'
      if (this.$mcToast?.error) this.$mcToast.error(message)
      else if (this.$toast?.error) this.$toast.error(message)
    },
    onFilterChange(filter) {
      this.selectedFilter = filter
      this.currentPage = 1
      this.fetchPackages()
    },
    onSearchInput() {
      clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => {
        this.currentPage = 1
        this.fetchPackages()
      }, 400)
    },
    onPerPageChange() {
      this.currentPage = 1
      this.fetchPackages()
    },
    changePage(page) {
      this.currentPage = page
      this.fetchPackages()
    },
    handlePackageAssembled() {
      this.fetchPackageSummary()
      this.fetchPackages()
    },
    buildPackageListParams() {
      const params = {
        page: this.currentPage,
        limit: this.perPage,
      }

      const trimmedSearch = typeof this.search === 'string' ? this.search.trim() : ''
      if (trimmedSearch) params.search = trimmedSearch

      const activeFilter = packageFilters.find((item) => item.value === this.selectedFilter)
      if (activeFilter?.filter) params.filters = [activeFilter.filter]

      return params
    },
    buildPackageQcPayload(status) {
      return {
        qc_status: status,
      }
    },
    qcActions(packageItem) {
      if (packageItem.qc_status === 'QC_PENDING') return qcPendingActions
      if (packageItem.qc_status === 'QC_FAILED') return qcFailedActions
      return []
    },
    isPackageUpdating(packageItem, status) {
      return this.updatingPackage[packageItem.package_id] === status
    },
    async updatePackageQcStatus(packageItem, status) {
      this.$set(this.updatingPackage, packageItem.package_id, status)

      const res = await smsAxios
        .patch(`/package/${packageItem.package_id}`, this.buildPackageQcPayload(status))
        .catch((error) => {
          this.showError(error)
        })

      if (res) {
        packageItem.qc_status = status
        this.fetchPackages()
      }

      this.$delete(this.updatingPackage, packageItem.package_id)
    },
    statusVariant(status) {
      if (!status) return 'bg-secondary'
      if (status.includes('PASS')) return 'bg-success'
      if (status.includes('FAILED')) return 'bg-danger'
      return 'bg-warning'
    },
    filterCount(filter) {
      if (filter.value === 'all') return this.totalRows
      if (filter.value === 'in-stock') return this.summary.in_stock_sby + this.summary.in_stock_jkt
      if (filter.value === 'with-technicians') return this.summary.with_technicians
      if (filter.value === 'in-transit') return this.summary.in_transit
      if (filter.value === this.selectedFilter) return this.totalRows

      return 0
    },
  },
}
</script>

<template>
  <Layout>
    <div class="package-planner">
      <div class="package-planner__header">
        <PageHeader title="Packages" />
        <p>
          A Package is a GPS, a Simcard and optional items such as a Fuel sensor, Dashcam or BLE Sensor.
          It uses the GPS IMEI as its ID. GPS, Fuel sensor and Dashcam each carry their own configuration.
          Packages can be QC passed and still incomplete when required parts are missing.
        </p>
      </div>

      <AssemblePackage @assembled="handlePackageAssembled" />

      <b-alert show variant="primary" class="package-planner__notice">
        <strong>In Stock</strong> means QC Passed, Complete, not allocated to a WO, and in a warehouse (Sby or Jkt).
        Pending, Failed and Incomplete Packages cannot be allocated, so they cannot reach a technician.
      </b-alert>

      <b-row class="package-planner__summary">
        <b-col
          v-for="item in packageSummary"
          :key="item.label"
          xl="2"
          md="4"
          sm="6"
          class="mb-3"
        >
          <div class="package-planner__metric">
            <strong>
              <b-spinner v-if="isSummaryLoading" small />
              <template v-else>{{ item.value }}</template>
            </strong>
            <span>{{ item.label }}</span>
          </div>
        </b-col>
      </b-row>

      <div class="package-planner__filters" role="group" aria-label="Package filters">
        <label class="package-planner__search">
          <i class="fas fa-search"></i>
          <b-form-input
            v-model="search"
            type="search"
            placeholder="Search package"
            @input="onSearchInput"
          />
        </label>
        <b-button
          v-for="filter in filters"
          :key="filter.value"
          pill
          :variant="selectedFilter === filter.value ? 'primary' : 'outline-secondary'"
          @click="onFilterChange(filter.value)"
        >
          {{ filter.text }}
          <span class="package-planner__filter-count">{{ filterCount(filter) }}</span>
        </b-button>
      </div>

      <div class="package-planner__table-wrap">
        <table class="package-planner__table">
          <thead>
            <tr>
              <th>Imei</th>
              <th>Package Name</th>
              <th>QC Status</th>
              <th>Custom Profile</th>
              <th>Location</th>
              <th>Allocated to</th>
              <th>Technician</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="packageItem in packages" :key="packageItem.package_id">
              <td>
                <strong>{{ packageItem.imei }}</strong>
              </td>
              <td>
                {{ packageItem.package_template_name }}
              </td>
              <td>
                <b-badge :class="statusVariant(packageItem.qc_status)">
                  {{ packageItem.qc_status }}
                </b-badge>
              </td>
              <td>{{ packageItem.custom_profile_name }}</td>
              <td>
                <strong>{{ packageItem.location }}</strong>
              </td>
              <td>{{ packageItem.work_order_plan_number ?? 'None' }}</td>
              <td>{{ packageItem.technician_name ?? 'None' }}</td>
              <td>
                <div class="package-planner__actions">
                  <b-button
                    v-for="action in qcActions(packageItem)"
                    :key="action.status"
                    size="sm"
                    :variant="action.variant"
                    :disabled="Boolean(updatingPackage[packageItem.package_id])"
                    @click="updatePackageQcStatus(packageItem, action.status)"
                  >
                    <b-spinner
                      v-if="isPackageUpdating(packageItem, action.status)"
                      small
                    />
                    <span v-else>{{ action.text }}</span>
                  </b-button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <div v-if="isLoading" class="package-planner__empty">
          <b-spinner small />
          Loading packages...
        </div>

        <div v-else-if="packages.length === 0" class="package-planner__empty">
          No packages match this filter.
        </div>
      </div>

      <div class="package-planner__table-footer">
        <div class="package-planner__page-size">
          <span>Show</span>
          <b-form-select
            v-model="perPage"
            size="sm"
            :options="pageOptions"
            @change="onPerPageChange"
          />
          <span>entries</span>
        </div>
        <div class="package-planner__page-info">
          Showing {{ paginationStart }} to {{ paginationEnd }} of <strong>{{ totalRows }}</strong> entries
        </div>
         <div class="col-sm-12 col-md-6">
          <div class="dataTables_paginate paging_simple_numbers float-end">
            <ul class="pagination pagination-rounded mb-0">
              <b-pagination
                v-model="currentPage"
                :total-rows="totalRows"
                :per-page="perPage"
                align="end"
                size="md"
                @change="changePage"
              />
            </ul>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<style scoped>
.package-planner {
  color: #344054;
}

.package-planner__header {
  max-width: 760px;
  margin-bottom: 18px;
}

.package-planner__header p,
.package-planner__notice {
  color: #667085;
  font-size: 14px;
  line-height: 1.6;
}

.package-planner__notice {
  margin: 18px 0;
  border: 0;
  border-radius: 8px;
}

.package-planner__summary {
  margin-bottom: 2px;
}

.package-planner__metric {
  min-height: 92px;
  padding: 18px;
  border: 1px solid #e4e7ec;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 1px 2px rgba(16, 24, 40, .05);
}

.package-planner__metric strong,
.package-planner__metric span {
  display: block;
}

.package-planner__metric strong {
  color: #111827;
  font-size: 28px;
  line-height: 1;
}

.package-planner__metric span {
  margin-top: 10px;
  color: #667085;
  font-size: 13px;
}

.package-planner__search {
  flex: 1 1 280px;
  position: relative;
  width: 100%;
  max-width: 420px;
  margin: 0;
}

.package-planner__search i {
  position: absolute;
  z-index: 2;
  top: 50%;
  left: 14px;
  color: #98a2b3;
  transform: translateY(-50%);
}

.package-planner__search .form-control {
  padding-left: 40px;
}

.package-planner__filters {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  margin: 0 0 18px;
}

.package-planner__filter-count {
  margin-left: 6px;
  opacity: .8;
}

.package-planner__table-wrap {
  overflow-x: auto;
  border: 1px solid #e4e7ec;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 1px 2px rgba(16, 24, 40, .05);
}

.package-planner__table {
  width: 100%;
  min-width: 920px;
  border-collapse: collapse;
}

.package-planner__table th,
.package-planner__table td {
  padding: 16px 18px;
  border-bottom: 1px solid #eef2f6;
  vertical-align: middle;
  text-align: left;
}

.package-planner__table th {
  color: #667085;
  background: #f9fafb;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: .02em;
  text-transform: uppercase;
  white-space: nowrap;
}

.package-planner__table td {
  color: #344054;
  font-size: 14px;
}

.package-planner__table tbody tr:last-child td {
  border-bottom: 0;
}

.package-planner__content {
  display: block;
  margin-top: 4px;
  color: #667085;
  font-family: Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  font-size: 12px;
}

.package-planner__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  min-width: 220px;
}

.package-planner__empty {
  display: flex;
  gap: 8px;
  justify-content: center;
  padding: 28px;
  color: #667085;
  text-align: center;
}

.package-planner__table-footer {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
  justify-content: space-between;
  margin-top: 0;
  padding: 12px 14px;
  border: 1px solid #e4e7ec;
  border-top: 0;
  border-radius: 0 0 8px 8px;
  background: #fff;
  box-shadow: 0 1px 2px rgba(16, 24, 40, .05);
}

.package-planner__page-size {
  display: inline-flex;
  gap: 8px;
  align-items: center;
  color: #667085;
  font-size: 14px;
}

.package-planner__page-size .custom-select {
  width: 80px;
}

.package-planner__page-info {
  color: #667085;
  font-size: 14px;
}

.package-planner__table-footer::v-deep .pagination {
  margin-bottom: 0;
}

.package-planner::v-deep .custom-select,
.package-planner::v-deep .form-control {
  min-height: 44px;
  border-color: #d0d5dd;
  border-radius: 8px;
  color: #344054;
  background-color: #fff;
}

.package-planner::v-deep .custom-select-sm {
  min-height: 34px;
  border-radius: 6px;
}

@media (max-width: 767.98px) {
  .package-planner__metric {
    min-height: 82px;
  }

  .package-planner__filters .btn {
    flex: 1 1 auto;
  }

  .package-planner__search {
    max-width: none;
  }

  .package-planner__table-footer {
    align-items: stretch;
  }

  .package-planner__table-footer .pagination {
    justify-content: flex-start;
  }
}
</style>
